// src/components/tabs/DiagnosticTab.tsx

import React, { useEffect, useState } from "react";
import {
  Alert,
  Card,
  List,
  Badge,
  Tag,
  Space,
  Row,
  Col,
  Divider,
} from "antd";
import {
  WarningOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  DatabaseOutlined,
  ClearOutlined,
  ToolOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { getHealthColor, formatBytes } from "../utils/storage.utils";
import { useStorageData } from "../hooks/useStorageData";

interface DeviceInfo {
  model: string;
  platform: string;
  version: string;
  userAgent: string;
}

export const DiagnosticTab: React.FC = () => {
  const { databases, issues, loadDatabases } = useStorageData();
  const [deviceInfo, setDeviceInfo] = useState<{
    model: string;
    platform: string;
    version: string;
    userAgent: string;
  } | null>(null);

  useEffect(() => {
    console.log('abriendo desde diagnostico')
    loadDatabases();
    getDeviceInfo().then((info: any) => setDeviceInfo(info));
  }, []);

  const getDeviceInfo = (): Promise<DeviceInfo> => {
    // Usar navigator.userAgentData si está disponible (API más moderna)
    if ("userAgentData" in navigator && navigator.userAgentData) {
      return (navigator.userAgentData as any)
        .getHighEntropyValues(["model", "platform", "platformVersion"])
        .then((ua: any) => {
          return {
            model: ua.model || "Desconocido",
            platform: ua.platform || "Desconocido",
            version: ua.platformVersion || "Desconocido",
            userAgent: navigator.userAgent, // Fallback para más detalles
          };
        })
        .catch(() => {
          // Fallback si userAgentData falla
          return {
            model: "Desconocido",
            platform: "Desconocido",
            version: "Desconocido",
            userAgent: navigator.userAgent,
          };
        });
    } else {
      // Fallback a navigator.userAgent si userAgentData no está disponible
      const ua = navigator.userAgent;
      let model = "Desconocido";
      let platform = "Desconocido";

      if (/Android/i.test(ua)) {
        platform = "Android";
        const match = ua.match(/Android\s([0-9\.]*)/);
        const version = match ? match[1] : "Desconocido";
        const modelMatch = ua.match(/\((.*?)\)/);
        model = modelMatch
          ? modelMatch[1].split(";")[1]?.trim() || "Desconocido"
          : "Desconocido";
        return Promise.resolve({ model, platform, version, userAgent: ua });
      } else if (/iPhone|iPad|iPod/i.test(ua)) {
        platform = "iOS";
        const versionMatch = ua.match(/OS\s([0-9_]*)/);
        const version = versionMatch
          ? versionMatch[1].replace(/_/g, ".")
          : "Desconocido";
        model = /iPhone/.test(ua)
          ? "iPhone"
          : /iPad/.test(ua)
          ? "iPad"
          : "iPod";
        return Promise.resolve({ model, platform, version, userAgent: ua });
      } else {
        return Promise.resolve({
          model: "Desconocido",
          platform: "Desconocido",
          version: "Desconocido",
          userAgent: ua,
        });
      }
    }
  };

  const getHealthIcon = (status: string) => {
    switch (status) {
      case "healthy":
        return <CheckCircleOutlined />;
      case "warning":
        return <WarningOutlined />;
      case "error":
        return <WarningOutlined />;
      default:
        return <InfoCircleOutlined />;
    }
  };

  const recommendations = [
    {
      icon: <DatabaseOutlined style={{ color: "#1890ff" }} />,
      title: "Sistema Unificado",
      description: "Utilizando sistema de almacenamiento unificado optimizado.",
    },
    {
      icon: <ClearOutlined style={{ color: "#52c41a" }} />,
      title: "Limpieza Regular",
      description:
        "Ejecuta limpieza de datos antiguos periódicamente (>30 días).",
    },
    {
      icon: <WarningOutlined style={{ color: "#faad14" }} />,
      title: "Monitoreo de Cuota",
      description:
        "Supervisa el uso de almacenamiento para evitar limitaciones.",
    },
    {
      icon: <ToolOutlined style={{ color: "#722ed1" }} />,
      title: "Reparación Preventiva",
      description:
        "Ejecuta diagnósticos regulares para mantener la integridad.",
    },
    {
      icon: <SyncOutlined style={{ color: "#1890ff" }} />,
      title: "Sincronización Activa",
      description:
        "Mantén los datos sincronizados con el servidor regularmente.",
    },
  ];

  return (
    <>
      {/* Problemas detectados */}
      {issues.length > 0 && (
        <Alert
          message={`${issues.length} problema(s) detectado(s)`}
          description={
            <List
              size="small"
              dataSource={issues}
              renderItem={(issue) => (
                <List.Item>
                  <WarningOutlined
                    style={{ color: "#faad14", marginRight: 8 }}
                  />
                  {issue}
                </List.Item>
              )}
            />
          }
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Estado de las bases de datos */}
      <Card title="Bases de Datos IndexedDB" style={{ marginBottom: 16 }}>
        {databases.length > 0 ? (
          <List
            dataSource={databases}
            renderItem={(db) => (
              <List.Item>
                <List.Item.Meta
                  avatar={getHealthIcon(db.status)}
                  title={
                    <Space>
                      <span>{db.name}</span>
                      <Tag color={getHealthColor(db.status)}>v{db.version}</Tag>
                      <Badge count={db.itemCount} showZero color="#1890ff" />
                    </Space>
                  }
                  description={
                    <div>
                      <div>
                        <strong>Stores:</strong>{" "}
                        {db.stores.join(", ") || "Ninguno"}
                      </div>
                      <div>
                        <strong>Elementos:</strong> {db.itemCount} •{" "}
                        <strong>Tamaño estimado:</strong>{" "}
                        {formatBytes(db.estimatedSize)}
                      </div>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        ) : (
          <div style={{ textAlign: "center", padding: 20, color: "#999" }}>
            No se encontraron bases de datos adicionales
          </div>
        )}
      </Card>

      {/* Recomendaciones */}
      <Card title="Recomendaciones" type="inner">
        <List
          size="small"
          dataSource={recommendations}
          renderItem={(item) => (
            <List.Item>
              <List.Item.Meta
                avatar={item.icon}
                title={item.title}
                description={item.description}
              />
            </List.Item>
          )}
        />
      </Card>

      {/* Información del navegador */}
      <Card
        title="Información del Navegador"
        type="inner"
        style={{ marginTop: 16 }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <div>
              <strong>IndexedDB:</strong>{" "}
              <Tag color={"indexedDB" in window ? "green" : "red"}>
                {"indexedDB" in window ? "Soportado" : "No soportado"}
              </Tag>
            </div>
          </Col>
          <Col span={12}>
            <div>
              <strong>Service Worker:</strong>{" "}
              <Tag color={"serviceWorker" in navigator ? "green" : "red"}>
                {"serviceWorker" in navigator ? "Soportado" : "No soportado"}
              </Tag>
            </div>
          </Col>
        </Row>
        <Divider />
        <Row gutter={16}>
          <Col span={12}>
            <div>
              <strong>Storage API:</strong>{" "}
              <Tag
                color={
                  (navigator.storage?.estimate as any) ? "green" : "orange"
                }
              >
                {(navigator.storage?.estimate as any)
                  ? "Disponible"
                  : "Limitado"}
              </Tag>
            </div>
          </Col>
          <Col span={12}>
            <div>
              <strong>User Agent:</strong>{" "}
              <span style={{ fontSize: "11px", color: "#666" }}>
                {navigator.userAgent.split(" ").slice(0, 3).join(" ")}...
              </span>
            </div>
          </Col>
          <Divider />
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <div>
              <strong>Dispositivo:</strong>{" "}
              <Tag color="blue">{deviceInfo?.model ?? "Desconocido"}</Tag>
            </div>
          </Col>
          <Col span={12}>
            <div>
              <strong>Sistema Operativo:</strong>{" "}
              <span style={{ fontSize: "11px", color: "#666" }}>
                {`${deviceInfo?.platform ?? "Desconocido"} ${
                  deviceInfo?.version ?? "Desconocido"
                }`}
              </span>
            </div>
          </Col>
        </Row>
      </Card>
    </>
  );
};
